import { useEffect, useState } from 'react';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { baseApi, BaseApiError } from '../../../api/baseClient';

// RECOMPENSAS DE OTRA SUCURSAL (10-oct-2026, Bramarket). Por negocio (tenant_settings.loyalty_flags.other_branch_rewards):
// en el kiosco y la página del cliente, las recompensas exclusivas de OTRA sucursal se ocultan (por defecto) o se ven
// apagadas con «Solo en …» para invitar a visitarla. Canjearlas fuera de su sucursal lo impide el servidor siempre.
type Modo = 'hide' | 'show';
const OPCIONES: { v: Modo; t: string; d: string; I: typeof Eye }[] = [
  { v: 'hide', t: 'Ocultarlas', d: 'Cada sucursal solo enseña lo que se canjea ahí.', I: EyeOff },
  { v: 'show', t: 'Mostrarlas apagadas', d: '«Solo en B Coffee»: invita a visitar la otra sucursal.', I: Eye },
];

export default function OtherBranchRewardsToggle() {
  const [modo, setModo] = useState<Modo | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    baseApi.get<{ otherBranchRewards?: Modo }>('/loyalty/settings')
      .then((d) => { if (vivo) setModo(d.otherBranchRewards || 'hide'); })
      .catch(() => { if (vivo) setModo('hide'); });
    return () => { vivo = false; };
  }, []);

  const elegir = async (next: Modo) => {
    if (modo === null || saving || next === modo) return;
    setSaving(true); setError(null);
    try {
      const r = await baseApi.put<{ otherBranchRewards: Modo }>('/loyalty/other-branch-rewards', { mode: next });
      setModo(r.otherBranchRewards);
    } catch (e) {
      setError(e instanceof BaseApiError ? e.message : 'No se pudo guardar. Intenta de nuevo.');
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-lg border border-line bg-surface p-4" data-testid="otras-sucursales-toggle">
      <p className="text-sm font-medium text-content flex items-center gap-2">
        Recompensas exclusivas de otra sucursal, en el kiosco {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
      </p>
      <p className="text-xs text-content-3 mt-0.5">
        Solo aplica a las recompensas que marcaste para ciertas sucursales. Nunca se pueden canjear fuera de su sucursal.
      </p>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        {OPCIONES.map((o) => (
          <button key={o.v} type="button" onClick={() => elegir(o.v)} disabled={modo === null || saving}
            data-testid={`otras-sucursales-${o.v}`} aria-pressed={modo === o.v}
            className={`rounded-lg border px-3 py-2 text-left transition disabled:opacity-60 ${modo === o.v ? 'border-green-600 bg-green-600/10' : 'border-line hover:bg-surface-2'}`}>
            <span className="flex items-center gap-1.5 text-sm font-semibold text-content"><o.I size={14} /> {o.t}</span>
            <span className="block text-[11px] text-content-3">{o.d}</span>
          </button>
        ))}
      </div>
      {error && <p className="text-xs text-red-600 dark:text-red-400 mt-1">{error}</p>}
    </div>
  );
}
